// THE LETTER STEP'S WORDS, AND THE OVERLAY'S, ARE THE APP'S (WEB-PARITY-5, R223). Tyler, 2026-09-29:
// "the site should replicate exactly what the app has at this time".
//
// Every label, heading, sentence and button on `/you` (the app's step 6, GenerateView), on `/building` (the
// LetterGenerationOverlay the app draws over it) and in the contact sheet `/letter` opens ("Your contact info",
// OwnerProfileEditor) that the server does not send is the app's own string, copied unchanged from the line it cites
// (under `AppealAce/`), as `parity4Wording.ts` is for steps 4 and 5. A `{name}` is the app's own `\(…)`
// interpolation; where the app chooses a word inside one (`\(n == 1 ? "sale" : "sales")`), each word is its own entry
// on the same line. `test/parity5.test.tsx` reads each cited line and fails when the words differ.
//
// ONE IS ADAPTED, AND SAYS SO (`cut`, as WEB-PARITY-6's are): the overlay's note "This usually takes a few minutes. You
// can leave the app — the letter finishes without it, and it will be here when you come back."
// (LetterGenerationOverlay.swift:226) keeps its first sentence and cuts the second, which names an app the website's
// owner does not have. The test checks the kept words are inside the cited literal and that the cut is named.
//
// TWO LEAD THE APP, AND SAY SO (`leads`): the overlay's long-wait notice (LetterGenerationOverlay.swift:147) and the retry
// button (GenerateView.swift:1197). Tyler approved new words for the first on 2026-10-13, choosing between two drafts: "I
// want that one.." (`LONG_WAIT_APPROVED`; R251), and for the second the same day, answering whether
// the button should be renamed to match his approved failure sentence "…Press Try again and we'll write it fresh, at no
// cost…": 'button becomes "Try again" - good' (`TRY_AGAIN_APPROVED`; R252). The website shows them now; the
// app's line still holds the old words, which `replaces` keeps word for word, and the app catches up in its next release
// under the same approval. `test/parity5.test.tsx` holds both halves: the cited line still holds `replaces`, and not yet
// `text`. When the app ships them, that test goes red on purpose: the entry then becomes `app(…)` again.
//
// NOT HERE, ON PURPOSE (each in docs/WEB_READINESS.md's WEB-PARITY-5 section):
//   - "No time right now? Closing the app is safe — …" (LetterGenerationOverlay.swift:253): the whole sentence is about
//     closing the app;
//   - the cap-regime line under the requested value (GenerateView.swift:696): no served state has a cap regime, as
//     WEB-PARITY-4 left its card;
//   - "Credit required", "1 credit", "File anyway — uses 1 credit", "Use a credit to generate" and the upsell sheet
//     (GenerateView.swift:370, :508, :1175, :1655-1736): every page here is behind the spend, so the app's AI path is
//     the only one;
//   - the generation cap's own sentence (GenerateView.swift:1255): it promises a hand edit the app does not have (D1,
//     D9), and the server refuses a queue past the cap in its own words;
//   - the raising-ask block's sentence (GenerateView.swift:1244): the server refuses that queue itself (G-6), in its own
//     words, and the refusal is shown as every refusal is.

import { WEB_PARITY, type AppWording } from "../../components/heldCopy";

/** The app's words with a sentence cut from beside them. `cut` names what was cut and why. */
export interface AdaptedAppWording extends AppWording {
  readonly cut: string;
}

function app(appSource: string, text: string): AppWording {
  return { text, status: "appWording", approval: WEB_PARITY, appSource };
}

function adapted(appSource: string, text: string, cut: string): AdaptedAppWording {
  return { text, status: "appWording", approval: WEB_PARITY, appSource, cut };
}

/** Tyler's approval of the overlay's new long-wait words (2026-10-13), choosing between two drafts. */
export const LONG_WAIT_APPROVED = { by: "Tyler", on: "2026-10-13", quote: "I want that one.." } as const;

/** Tyler's approval of the retry button's new name (2026-10-13), answering whether to rename it so it matches his approved
 *  failure sentence ("…Press Try again and we'll write it fresh, at no cost…", `letter.failed.letterValidationFailed`). */
export const TRY_AGAIN_APPROVED = { by: "Tyler", on: "2026-10-13", quote: 'button becomes "Try again" - good' } as const;

/** WORDS THE WEBSITE SHOWS AHEAD OF THE APP: approved for both, shipped here first. `appSource` is the app's line they
 *  replace; `replaces` is what that line still says until the app's next release. */
export interface LeadingWording {
  readonly text: string;
  readonly status: "approvedLeadsApp";
  readonly approval: typeof LONG_WAIT_APPROVED | typeof TRY_AGAIN_APPROVED;
  readonly appSource: string;
  readonly replaces: string;
}

function leads(
  approval: LeadingWording["approval"],
  appSource: string,
  text: string,
  replaces: string,
): LeadingWording {
  return { text, status: "approvedLeadsApp", approval, appSource, replaces };
}

const GV = "Features/Generate/GenerateView.swift";
const LO = "Features/Generate/LetterGenerationOverlay.swift";
const OP = "Features/Tabs/OwnerProfileEditor.swift";
const MOD = "Models/Models.swift";
const ET = "Shared/EntitlementTag.swift";

export const GENERATE_WORDING = {
  // ══ the hero and the path card (GenerateView.swift:336-391) ══
  "gen.title": app(`${GV}:346`, "Generate your letter"),
  "gen.body": app(
    `${GV}:356`,
    "Review what you've built, pick a tone, and we'll produce a board-ready letter — this usually takes a few minutes.\n\nYou won't be stuck with the first draft: once it's written you can tell us what reads wrong and we'll edit it. Facts are best settled here first — the letter can only argue what your appeal actually contains.",
  ),
  "gen.path.title": app(`${GV}:370`, "PROppeal letter"),
  "gen.path.body": app(`${GV}:377`, "PROppeal writes a custom one-page letter citing your comps, reasons, and photos."),
  "gen.path.tag": app(`${ET}:37`, "Included"),

  // ══ the honest fork (YU-9; GenerateView.swift:393-555) ══
  "gen.fork.thinData.title": app(`${GV}:404`, "We couldn't find enough recent sales near your home yet"),
  "gen.fork.noCase.title": app(`${GV}:405`, "Your home looks fairly assessed — we're not finding a case"),
  "gen.fork.sales.none": app(`${GV}:413`, "we haven't turned up recent nearby sales from our licensed data partners"),
  "gen.fork.sales.some": app(`${GV}:414`, "our licensed data partners only turned up {n} nearby {sales}"),
  "gen.fork.sales.sale": app(`${GV}:414`, "sale"),
  "gen.fork.sales.sales": app(`${GV}:414`, "sales"),
  "gen.fork.lead.nonDisclosure": app(
    `${GV}:416`,
    "Your state doesn't require sale prices to be made public, so {salesPhrase} — not enough on their own to build a strong case automatically.",
  ),
  "gen.fork.lead.default": app(
    `${GV}:417`,
    "So far {salesPhrase} close enough to compare — not enough on their own to build a strong case automatically.",
  ),
  "gen.fork.thinData.body": app(
    `${GV}:418`,
    "{lead} Know of a home nearby that sold recently for less? Add it below and we'll fold it into your appeal — even one or two real sales can make the difference, and your analysis is already paid for.",
  ),
  "gen.fork.noCase.body": app(
    `${GV}:420`,
    "We reviewed {n} recent sales near you. Adjusted for size, they land at or above your current assessed value — an appeal usually only helps when comparable sales come in below your assessment. This is the professional read your credit paid for. Think we missed something? If you have a lower recent sale we didn't have, or real condition or record issues, add them and we'll re-check — and we'll be straight with you either way.",
  ),
  "gen.fork.better": app(`${GV}:440`, "What works better here"),
  "gen.fork.point.condition": app(
    `${GV}:446`,
    "Condition or damage — foundation cracks, a worn roof, water damage, or dated systems. Things comps can't capture.",
  ),
  "gen.fork.point.record": app(
    `${GV}:448`,
    "A factual record error — wrong square footage, bed/bath count, or year built on the county's card.",
  ),
  "gen.fork.point.sale": app(
    `${GV}:450`,
    "Know of a lower recent sale we didn't have? Add it and we'll re-check — and tell you straight if it still doesn't support a reduction.",
  ),
  "gen.weak.body": app(
    `${GV}:480`,
    "This case rests on a limited set of sales. Adding one or two more comparables you know of will make it stronger.",
  ),
  "gen.weak.add": app(`${GV}:484`, "Add a comparable sale"),
  "gen.forkBar.addSale": app(`${GV}:505`, "Add a comparable sale"),
  "gen.forkBar.addReasons": app(`${GV}:506`, "Add condition or record reasons"),
  "gen.forkBar.lowerSale": app(`${GV}:509`, "I know of a lower sale — add it"),
  "gen.forkBar.fileAnyway": app(`${GV}:513`, "File anyway with what I have"),
  "gen.forkBar.confirm": app(`${GV}:523`, "Confirm the boxes above to file anyway."),

  // ══ "Your case" (YU-4; GenerateView.swift:611-863) ══
  "gen.case.title": app(`${GV}:621`, "Your case"),
  "gen.case.lead": app(`${GV}:628`, "Everything your appeal rests on, in one place. Review it before you adopt it as your own below."),
  "gen.case.stale": app(
    `${GV}:643`,
    "Your comparable sales changed after the honest case check ran — step back to it for an updated read. The figures below are current.",
  ),
  "gen.case.check": app(`${GV}:742`, "Honest case check"),
  "gen.case.property": app(`${GV}:655`, "Property"),
  "gen.case.assessed": app(`${GV}:665`, "Assessed"),
  "gen.case.requested": app(`${GV}:674`, "Requested"),
  "gen.case.requestedRaises": app(`${GV}:674`, "Requested — {note}"),
  "gen.case.wouldNotLower": app(`${MOD}:2292`, "would not lower your bill"),
  "gen.case.reduction": app(`${GV}:689`, "Requested reduction"),
  "gen.case.reductionAssessed": app(`${GV}:689`, "Requested reduction (assessed)"),
  "gen.case.reductionValue": app(`${GV}:690`, "{amount} (−{percent}%)"),
  "gen.case.comps": app(`${GV}:773`, "Comparable sales cited"),
  "gen.case.noComps": app(`${GV}:782`, "No comps cited — this appeal rests on condition and record grounds."),
  "gen.case.fewer": app(`${GV}:809`, "Show fewer"),
  "gen.case.more": app(`${GV}:809`, "+ {n} more"),
  "gen.case.grounds": app(`${GV}:825`, "Grounds for the appeal"),
  "gen.case.noGrounds": app(`${GV}:829`, "None selected yet."),
  // `basisTagged` (Models.swift:1245-1248): a figure on a fractional basis names its basis.
  "gen.basis.tagged": app(`${MOD}:1247`, "{value} {basis}"),
  "gen.basis.market": app(`${MOD}:1247`, "market"),
  "gen.basis.assessed": app(`${MOD}:1247`, "assessed"),
  // The two reason titles the Value step's four tiles do not carry (`AppealReason.title`); the other four are
  // `parity4Wording.ts`'s, on the same lines.
  "gen.reason.uniformity": app(`${MOD}:182`, "Unequal assessment (uniformity)"),
  "gen.reason.incomeApproach": app(`${MOD}:183`, "Income the property produces"),

  // ══ the compliance card's own line (YU-5; GenerateView.swift:1160) — its title and body are G-2's ══
  "gen.disclaimer": app(
    `${GV}:1160`,
    "PROppeal is a self-help tool. This is your estimated, comp-supported value — not an appraisal by a licensed appraiser, and not legal advice or a substitute for an attorney.",
  ),

  // ══ "How should it sound?" (YU-6; GenerateView.swift:867-896, `LetterTone`, Models.swift:2891-2921) ══
  "gen.tone.title": app(`${GV}:873`, "How should it sound?"),
  "gen.tone.formal": app(`${MOD}:2900`, "Formal"),
  "gen.tone.respectful": app(`${MOD}:2901`, "Respectful"),
  "gen.tone.direct": app(`${MOD}:2902`, "Direct"),
  "gen.tone.formal.blurb": app(`${MOD}:2908`, "Traditional business-letter tone. Neutral, precise."),
  "gen.tone.respectful.blurb": app(`${MOD}:2909`, "Softer, more deferential phrasing. Hat-in-hand."),
  "gen.tone.direct.blurb": app(`${MOD}:2910`, "Fact-forward and confident, but still appropriate."),

  // ══ the bar under it all (YU-10; GenerateView.swift:1056-1104, :1163-1180) ══
  "gen.helper": app(
    `${GV}:1066`,
    "Your appeal will be analyzed and the letter custom-written — this usually takes a few minutes. You'll be able to ask for wording changes afterwards.",
  ),
  "gen.confirm": app(`${GV}:1068`, "Confirm the boxes above to continue."),
  "gen.left": app(
    `${GV}:1079`,
    "{left} of {max} letter generations left for this appeal — rewriting is free and won't use another credit or comp search.",
  ),
  "gen.generate": app(`${GV}:1198`, "Generate appeal letter"),
  // Tyler, 2026-10-13: 'button becomes "Try again" - good' — the website leads; the app's line catches up in its next release.
  "gen.retry": leads(TRY_AGAIN_APPROVED, `${GV}:1197`, "Try again", "Try generating again"),

  // ══ a letter that did not finish, on Generate itself (BL-4; GenerateView.swift:966-1041) ══
  "gen.error.question": app(`${GV}:974`, "Is there something we don't know about your home?"),
  "gen.error.title": app(`${GV}:975`, "We couldn't finish this letter"),
  "gen.error.questionLead": app(`${GV}:987`, "We can only argue from what we can see. If any of these is true, it changes the case:"),
  "gen.error.floor": app(
    `${GV}:1030`,
    "Only if it's genuinely true. A claim we can't stand behind is worth less than not filing — and in some states a board can raise an assessment on the owner's own appeal.",
  ),

  // ══ the overlay (BL-1, BL-5; LetterGenerationOverlay.swift, built by GenerateView.swift:1542-1601) ══
  "build.title": app(`${GV}:1575`, "Building your appeal"),
  "build.row.property": app(`${GV}:1553`, "Reading your property details"),
  "build.row.comps": app(`${GV}:1555`, "Analyzing {n} comparable {sales}"),
  "build.row.comps.sale": app(`${GV}:1555`, "sale"),
  "build.row.comps.sales": app(`${GV}:1555`, "sales"),
  "build.row.compare": app(`${GV}:1556`, "Comparing assessed vs. market value"),
  "build.row.assessed": app(`${GV}:1558`, "Reviewing the assessed value"),
  "build.row.reasons": app(`${GV}:1564`, "Weighing all your appeal reasons"),
  "build.row.condition": app(`${GV}:1567`, "Factoring in condition notes"),
  "build.row.photos": app(`${GV}:1570`, "Reviewing {n} photo {exhibits}"),
  "build.row.photos.exhibit": app(`${GV}:1570`, "exhibit"),
  "build.row.photos.exhibits": app(`${GV}:1570`, "exhibits"),
  "build.row.tone": app(`${GV}:1572`, "Writing your letter in a {tone} tone"),
  "build.row.format": app(`${GV}:1573`, "Formatting and finalizing"),
  "build.final.1": app(`${GV}:1587`, "Formatting and finalizing"),
  "build.final.2": app(`${GV}:1588`, "Checking every figure traces to your data"),
  "build.final.3": app(`${GV}:1589`, "Reviewing the wording for accuracy"),
  "build.final.4": app(`${GV}:1595`, "Second reader is checking the case for weak spots"),
  "build.final.5": app(`${GV}:1596`, "Making sure a board can follow your argument"),
  "build.final.6": app(`${GV}:1597`, "Laying out your exhibits"),
  "build.final.7": app(`${GV}:1598`, "Almost there — finishing your letter"),
  "build.queued": app(`${LO}:133`, "Queued — your letter is next in line"),
  // Tyler, 2026-10-13: "I want that one.." — the website leads; the app's line catches up in its next release.
  "build.longWait": leads(
    LONG_WAIT_APPROVED,
    `${LO}:147`,
    "Great letters take a little time. PROppeal checks every number against your sales and polishes anything that isn’t right before it’s ready. You can leave this page — your letter will be saved to this appeal, and it’s already paid for.",
    "This one's taking a little longer — usually that means the review found something worth rewriting, and it's redoing it properly. Nothing is at risk if you leave: the letter is being written on our servers and will be saved to this appeal either way. This appeal is already paid for, and trying again never costs another credit.",
  ),
  "build.note": adapted(
    `${LO}:226`,
    "This usually takes a few minutes.",
    "the second sentence, \"You can leave the app — the letter finishes without it, and it will be here when you come back.\": the website's owner has no app to leave",
  ),
  "build.ready": app(`${LO}:291`, "Your letter is ready"),
  "build.readySub": app(`${LO}:296`, "Everything above went into your appeal."),
  "build.progress": app(`${LO}:391`, "Reviewing your appeal…"),
  "build.complete": app(`${LO}:391`, "Complete"),

  // ══ "Your contact info" (D3, YU-7; OwnerProfileEditor.swift:12-112), opened from Review & Export ══
  "contact.title": app(`${OP}:103`, "Your contact info"),
  "contact.save": app(`${OP}:108`, "Save"),
  "contact.name.header": app(`${OP}:19`, "Name on the letter"),
  "contact.name.placeholder": app(`${OP}:15`, "Full name"),
  "contact.name.footer": app(`${OP}:21`, "Used in the signature block and as the first-person author."),
  "contact.find.header": app(`${OP}:52`, "Find your address"),
  "contact.find.placeholder": app(`${OP}:26`, "Search your address"),
  "contact.find.footer": app(
    `${OP}:54`,
    "Start typing to search, then pick your address — or just fill in the fields below by hand.",
  ),
  "contact.mailing.header": app(`${OP}:72`, "Mailing address"),
  "contact.street": app(`${OP}:59`, "Mailing street"),
  "contact.city": app(`${OP}:61`, "City"),
  "contact.state": app(`${OP}:64`, "State"),
  "contact.zip": app(`${OP}:67`, "ZIP"),
  "contact.mailing.footer": app(
    `${OP}:74`,
    "Appears in the letterhead so the board knows where to respond. Edit any field to override the search.",
  ),
  "contact.reach.header": app(`${OP}:96`, "Contact"),
  "contact.email": app(`${OP}:79`, "Email"),
  "contact.phone": app(`${OP}:84`, "Phone"),
  "contact.reach.footer": app(
    `${OP}:98`,
    "At least one of these should be filled in. Boards often need a way to reach you outside mail.",
  ),
} as const satisfies Record<string, AppWording | LeadingWording>;

export type GenerateWordingId = keyof typeof GENERATE_WORDING;

export function sayG(id: GenerateWordingId): string {
  return GENERATE_WORDING[id].text;
}

/** A sentence with its `{name}` slots filled. A slot left unfilled is a thrown error, never printed braces. */
export function sayGWith(id: GenerateWordingId, values: Readonly<Record<string, string | number>>): string {
  return sayG(id).replace(/\{(\w+)\}/g, (_, name: string) => {
    if (!Object.hasOwn(values, name)) throw new Error(`sayGWith(${id}): no value for {${name}}`);
    return String(values[name]);
  });
}

/** The overlay's last row, in the app's rotation order (GenerateView.swift:1586-1599). */
export const FINAL_STEP_MESSAGES: readonly GenerateWordingId[] = [
  "build.final.1", "build.final.2", "build.final.3", "build.final.4", "build.final.5", "build.final.6", "build.final.7",
];
